import { Box, Typography, Grid, Stack, Link, IconButton } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';

const TikTokIcon = () => (
  <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width={26} height={26} aria-hidden="true" focusable="false">
    <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
  </svg>
);

type FooterSectionProps = {
  page: 'landing' | 'menu';
};

const FooterSection = ({ page }: FooterSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const goToSection = (sectionId: string) => {
    if (page !== 'landing') {
      navigate('/');
      return;
    }
    if (sectionId === 'hero-section') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navLinks = [
    { key: 'home', section: 'hero-section' },
    { key: 'menu', section: 'menu-preview-section' },
    { key: 'location', section: 'location-section' },
  ].filter((link) => page === 'landing' || link.key === 'home');

  const socials = [
    { label: 'Instagram', href: 'https://www.instagram.com/tokki_creperie/', icon: <InstagramIcon /> },
    { label: 'TikTok', href: 'https://www.tiktok.com/@tokki.creperie', icon: <TikTokIcon /> },
  ];

  return (
    <Box
      id="footer-section"
      component="footer"
      sx={{
        width: '100%',
        px: { xs: 2, md: 5 },
        pt: { xs: 7, md: 9 },
        pb: { xs: 4, md: 5 },
        backgroundColor: tokens.color.cocoa,
        color: tokens.color.white,
      }}
    >
      <Grid container spacing={{ xs: 5, md: 4 }} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            {(t as any)('footer.links.title')}
          </Typography>
          <Stack spacing={0.5} alignItems="flex-start">
            {navLinks.map((link) => (
              // Real buttons. These were ListItems with an onClick — invisible
              // to keyboard and assistive tech, and decorated with a typed "•".
              <Link
                key={link.key}
                component="button"
                type="button"
                onClick={() => goToSection(link.section)}
                underline="none"
                sx={{
                  color: 'rgba(255,255,255,0.86)',
                  fontSize: '1.0625rem',
                  fontFamily: 'inherit',
                  py: 0.75,
                  cursor: 'pointer',
                  transition: `color ${tokens.duration.control} ease`,
                  '@media (hover: hover) and (pointer: fine)': {
                    '&:hover': { color: tokens.color.white, textDecoration: 'underline' },
                  },
                }}
              >
                {(t as any)(`links.${link.key}`)}
              </Link>
            ))}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            {(t as any)('footer.contact.title')}
          </Typography>

          {/* Was a Typography with an onClick assigning window.location. */}
          <Link
            href={`mailto:${(t as any)('footer.contact.email')}`}
            underline="none"
            sx={{
              color: 'rgba(255,255,255,0.86)',
              fontSize: '1.0625rem',
              transition: `color ${tokens.duration.control} ease`,
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': { color: tokens.color.white, textDecoration: 'underline' },
              },
            }}
          >
            {(t as any)('footer.contact.email')}
          </Link>

          <Stack direction="row" spacing={1} sx={{ mt: 2.5, ml: -1 }}>
            {socials.map((social) => (
              <IconButton
                key={social.label}
                component="a"
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                sx={{
                  color: 'rgba(255,255,255,0.86)',
                  '@media (hover: hover) and (pointer: fine)': {
                    '&:hover': { color: tokens.color.white, backgroundColor: 'rgba(255,255,255,0.1)' },
                  },
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Box
              component="img"
              src={`${process.env.REACT_APP_CLOUDFRONT_URL}/public/circularLogo.png`}
              alt=""
              loading="lazy"
              decoding="async"
              sx={{ height: 132, width: 132, objectFit: 'contain' }}
            />
          </Stack>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: { xs: 5, md: 6 },
          pt: 3,
          borderTop: '1px solid',
          borderColor: tokens.color.cocoaLine,
        }}
      >
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          © {new Date().getFullYear()} Tokki Creperie
        </Typography>
      </Box>
    </Box>
  );
}

export default FooterSection;
